import Bugsnag from '@bugsnag/js';
import {
  AccountApi,
  AdminApi,
  AuthApi,
  BankApi,
  BrokerApi,
  Configuration,
  DocumentApi,
  ERemissApi,
  GiddirApi,
  OrderApi,
  PaymentApi,
  ProductApi,
  QuizApi,
  SensitiveApi,
} from '../swagger';
import { getGAClientId, getIntercomId, UTM } from './analytics';

const LOCAL_API = `http://${window.location.hostname}:3000`;

export const API =
  process.env.NODE_ENV === 'production'
    ? 'https://api.unikaforsakringar.se'
    : LOCAL_API;

export const PROD_DOC = 'https://unikaforsakringar.se';

export const DOC = window.isProduction ? PROD_DOC : API;

export const apiConfiguration: Configuration = {};
export const getAuthHeaders = () => {
  const headers: Record<string, string> = {};

  if (typeof apiConfiguration.apiKey === 'string') {
    headers.Authorization = apiConfiguration.apiKey;
  }

  return headers;
};

const addHeaders = (init?: RequestInit) => {
  if (init) {
    const gaClientId = getGAClientId();
    const intercomId = getIntercomId();

    if (!init.headers) {
      init.headers = {};
    }

    if (gaClientId) {
      (init.headers as Record<string, string>)['X-GA'] = gaClientId;
    }

    if (intercomId) {
      (init.headers as Record<string, string>)['X-Intercom'] = intercomId;
    }

    (init.headers as Record<string, string>)['X-Lang'] =
      document.documentElement.lang;

    Object.assign(init.headers, UTM());
  }
};

let requestCounter = 0;
const fetchWithCredentials: typeof window.fetch = (url, init) => {
  try {
    addHeaders(init);
  } catch (e) {
    console.error(e);
    if (e instanceof Error) {
      Bugsnag.notify(e, (event) => {
        if (init) {
          event.addMetadata('init', init);
        }
      });
    }
  }

  const requestIndex = requestCounter;
  requestCounter++;

  return window.fetch(url, init).then((response) => {
    if (response.status === 401 && requestIndex !== 0) {
      window.alert('Session expired, please log in again');
    }

    return response;
  });
};

// Create instances of all API classes and merge them into a single api object
const accountApi = new AccountApi(apiConfiguration, API, fetchWithCredentials);
const adminApi = new AdminApi(apiConfiguration, API, fetchWithCredentials);
const authApi = new AuthApi(apiConfiguration, API, fetchWithCredentials);
const bankApi = new BankApi(apiConfiguration, API, fetchWithCredentials);
const brokerApi = new BrokerApi(apiConfiguration, API, fetchWithCredentials);
const documentApi = new DocumentApi(
  apiConfiguration,
  API,
  fetchWithCredentials,
);
const eRemissApi = new ERemissApi(apiConfiguration, API, fetchWithCredentials);
const giddirApi = new GiddirApi(apiConfiguration, API, fetchWithCredentials);
const orderApi = new OrderApi(apiConfiguration, API, fetchWithCredentials);
const paymentApi = new PaymentApi(apiConfiguration, API, fetchWithCredentials);
const productApi = new ProductApi(apiConfiguration, API, fetchWithCredentials);
const quizApi = new QuizApi(apiConfiguration, API, fetchWithCredentials);
const sensitiveApi = new SensitiveApi(
  apiConfiguration,
  API,
  fetchWithCredentials,
);

// Helper to bind all methods from an API instance
const bindMethods = <T extends object>(instance: T): T => {
  const proto = Object.getPrototypeOf(instance);
  const bound = {} as T;
  for (const key of Object.getOwnPropertyNames(proto)) {
    if (key !== 'constructor' && typeof (instance as any)[key] === 'function') {
      (bound as any)[key] = (instance as any)[key].bind(instance);
    }
  }
  return bound;
};

export const api = {
  ...bindMethods(accountApi),
  ...bindMethods(adminApi),
  ...bindMethods(authApi),
  ...bindMethods(bankApi),
  ...bindMethods(brokerApi),
  ...bindMethods(documentApi),
  ...bindMethods(eRemissApi),
  ...bindMethods(giddirApi),
  ...bindMethods(orderApi),
  ...bindMethods(paymentApi),
  ...bindMethods(productApi),
  ...bindMethods(quizApi),
  ...bindMethods(sensitiveApi),
} as AccountApi &
  AdminApi &
  AuthApi &
  BankApi &
  BrokerApi &
  DocumentApi &
  ERemissApi &
  GiddirApi &
  OrderApi &
  PaymentApi &
  ProductApi &
  QuizApi &
  SensitiveApi;

export const sendBeacon = (
  event: string,
  data: Record<string, string | number | undefined | boolean> = {},
) => {
  const formData = new FormData();
  const gaClientId = getGAClientId();
  const intercomId = getIntercomId();
  const completeData = { ...data, ...UTM() };

  for (const key of Object.keys(completeData)) {
    const value = completeData[key];
    if (value !== undefined && value !== '') {
      formData.append(key, `${value}`);
    }
  }

  if (gaClientId) {
    formData.append('X-GA', gaClientId);
  }
  if (intercomId) {
    formData.append('X-Intercom', intercomId);
  }
  if (apiConfiguration.apiKey) {
    formData.append('X-Authorization', `${apiConfiguration.apiKey}`);
  }

  if (window.navigator.sendBeacon) {
    window.navigator.sendBeacon(`${API}/log/${event}`, formData);
  }
};
